// ─── Grocery list: watchlist with auto sale-matching ───────────────────────
// Text quick-add ("chicken breast"). The app fuzzy-matches each item against
// weekly flyer deals; matches surface as "on sale now — $X at Y". In-app
// only: push notifications need server infra and are explicitly deferred
// (see README).
import React, { useState, useMemo } from "react";
import { T, money, buzzLight } from "./components";
import { IconPlus, IconX, IconTag, IconSearch, Mascot } from "./icons";
import { fmtDay } from "./weekly";

const LIST_KEY = "ctd-grocery-list-v1";

// ─── Fuzzy word matching ────────────────────────────────────────────────
function normWords(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 2)
    .map((w) =>
      w.endsWith("ies") ? w.slice(0, -3) + "y" : w.replace(/(es|s)$/, "")
    );
}

// Fraction of the list item's words found in the deal text.
export function matchScore(listText, dealText) {
  const lw = normWords(listText);
  if (!lw.length) return 0;
  const dw = new Set(normWords(dealText));
  const hits = lw.filter((w) => dw.has(w)).length;
  return hits / lw.length;
}

const isMatch = (listText, dealText) => matchScore(listText, dealText) >= 0.5;

// ─── Match a list against the weekly flyer deals ──────────────────────────
// Returns [{ item, matches: [{ref, label, sub}] }].
export function matchGroceryList(items, weeklyDeals, nowMs) {
  return items.map((item) => {
    const matches = [];
    for (const w of weeklyDeals) {
      if (new Date(w.validTo + "T23:59:59").getTime() < nowMs) continue;
      if (isMatch(item.text, `${w.item} ${w.store}`)) {
        const price = w.price != null ? money(w.price) + (w.unit || "") : w.headline;
        matches.push({
          ref: w,
          label: `${price} at ${w.store}`,
          sub: `Flyer to ${fmtDay(w.validTo)}`,
        });
      }
    }
    return { item, matches };
  });
}

// Number of list items with at least one live match (tab badge).
export function countListMatches(items, weeklyDeals, nowMs) {
  return matchGroceryList(items, weeklyDeals, nowMs).filter(
    (r) => r.matches.length > 0
  ).length;
}

// ─── Persistence ────────────────────────────────────────────────────────
function loadList() {
  try {
    const arr = JSON.parse(localStorage.getItem(LIST_KEY) || "[]");
    if (Array.isArray(arr)) return arr.filter((x) => x && x.text);
  } catch { /* ignore */ }
  return [];
}

export function useGroceryList() {
  const [items, setItems] = useState(loadList);
  const save = (next) => {
    setItems(next);
    try { localStorage.setItem(LIST_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };
  const add = (text) => {
    const t = text.trim();
    if (!t) return;
    if (items.some((i) => i.text.toLowerCase() === t.toLowerCase())) return;
    save([...items, { id: "g" + Date.now(), text: t, addedAt: Date.now() }]);
  };
  const remove = (id) => save(items.filter((i) => i.id !== id));
  return { items, add, remove };
}

// ─── The tab ────────────────────────────────────────────────────────────
export function GroceryListTab({ items, onAdd, onRemove, weeklyDeals, nowMs }) {
  const [draft, setDraft] = useState("");
  const rows = useMemo(
    () => matchGroceryList(items, weeklyDeals, nowMs),
    [items, weeklyDeals, nowMs]
  );
  const hitCount = rows.filter((r) => r.matches.length > 0).length;

  const submit = () => {
    if (draft.trim()) buzzLight();
    onAdd(draft);
    setDraft("");
  };

  return (
    <div>
      <div style={styles.head}>
        <div style={styles.title}>Grocery list</div>
        <div style={styles.sub}>
          Add what you buy — we'll flag it the moment it's on sale.
          {hitCount > 0 && (
            <span style={styles.hitPill} className="tnum"> · {hitCount} on sale now</span>
          )}
        </div>
      </div>

      <div style={styles.addRow}>
        <input
          style={styles.input}
          placeholder='e.g. chicken breast'
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          aria-label="Add a grocery item"
        />
        <button onClick={submit} style={styles.addBtn} aria-label="Add item">
          <IconPlus size={20} />
        </button>
      </div>

      {rows.length === 0 && (
        <div style={styles.empty}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Mascot size={84} />
          </div>
          <div style={{ fontWeight: 800, fontSize: 17, marginTop: 10, letterSpacing: -0.2 }}>
            Your list is a blank receipt.
          </div>
          <div style={{ fontSize: 13, color: T.textSub, marginTop: 5, lineHeight: 1.5 }}>
            Add staples you buy every week up top. When one shows up
            in the flyers, it lights up here.
          </div>
        </div>
      )}

      {rows.map(({ item, matches }) => (
        <div key={item.id} style={styles.row}>
          <div style={styles.rowHead}>
            <span style={styles.itemText}>{item.text}</span>
            <button onClick={() => onRemove(item.id)} style={styles.delBtn} aria-label={`Remove ${item.text}`}>
              <IconX size={15} />
            </button>
          </div>
          {matches.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              {matches.slice(0, 3).map((m, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.match,
                    // v2.1: match reveal animates in like a product demo
                    animation: "riseIn 0.45s cubic-bezier(0.22,1,0.36,1) both",
                    animationDelay: `${i * 90}ms`,
                  }}
                >
                  <IconTag size={13} />
                  <span>
                    <b>On sale now</b> — <span className="tnum">{m.label}</span>
                    <span style={styles.matchSub}> · {m.sub}</span>
                  </span>
                </div>
              ))}
              {matches.length > 3 && (
                <div style={styles.more}>+{matches.length - 3} more</div>
              )}
            </div>
          ) : (
            <div style={styles.watch}>
              <IconSearch size={13} /> Watching — not on sale right now
            </div>
          )}
        </div>
      ))}

      <div style={styles.fine}>
        Matches update whenever the weekly flyers refresh. Push alerts need
        server infrastructure — that's a later release; for now, check this tab.
      </div>
    </div>
  );
}

const styles = {
  head: { marginBottom: 16 },
  title: { fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 28, letterSpacing: -0.5 },
  sub: { fontSize: 12.5, color: T.textSub, marginTop: 4, lineHeight: 1.5 },
  hitPill: { color: T.accentDark, fontWeight: 700 },
  addRow: { display: "flex", gap: 8, marginBottom: 14 },
  input: {
    flex: 1, padding: 13, background: T.bgCard, border: `1.5px solid ${T.border}`,
    borderRadius: 13, color: T.text, fontSize: 16, outline: "none",
  },
  addBtn: {
    width: 50, borderRadius: 13, border: "none", background: T.accent, color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
    boxShadow: "0 4px 14px rgba(31,122,77,0.35)",
  },
  empty: {
    textAlign: "center", color: T.textSub, padding: "40px 20px",
    background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 18,
  },
  row: {
    background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 16,
    padding: "13px 14px", marginBottom: 10, boxShadow: T.shadow,
  },
  rowHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  itemText: { fontSize: 15, fontWeight: 700, textTransform: "capitalize" },
  delBtn: {
    background: T.bgCardAlt, border: `1px solid ${T.border}`, borderRadius: 9,
    width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", color: T.textMuted,
  },
  match: {
    display: "flex", gap: 8, alignItems: "flex-start", width: "100%", textAlign: "left",
    background: T.accentLight, border: `1px solid ${T.accentBorder}`, borderRadius: 11,
    padding: "9px 11px", marginTop: 6, fontSize: 13, color: T.accentDark,
    fontFamily: "inherit",
  },
  matchSub: { color: T.textSub, fontWeight: 500 },
  more: { fontSize: 12, color: T.textMuted, marginTop: 6, paddingLeft: 2 },
  watch: {
    display: "flex", gap: 6, alignItems: "center",
    fontSize: 12.5, color: T.textMuted, marginTop: 8,
  },
  fine: { fontSize: 11.5, color: T.textMuted, textAlign: "center", lineHeight: 1.6, marginTop: 18, padding: "0 12px" },
};
